import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle2, MessageSquare, Users, DollarSign, Activity, AlertCircle } from 'lucide-react';
import ragService from '../../api/services/ragService';

const Card = ({ icon: Icon, label, value, hint }) => (
  <div className="card-metric p-5">
    <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
      style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
      <Icon size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
    </div>
    <p className="text-label mb-1">{label}</p>
    <p className="font-metric text-ink" style={{ fontSize: '22px', fontWeight: 600, lineHeight: 1 }}>{value}</p>
    {hint && <p className="text-label text-xs mt-1">{hint}</p>}
  </div>
);

export default function RagDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await ragService.getMetrics();
        if (res.success) setMetrics(res.data);
      } catch (err) {
        setError(err.error?.message || err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-green" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>RAG</p>
        <h2 className="text-ink font-semibold" style={{ fontSize: '18px' }}>Dashboard RAG</h2>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-500" size={16} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {!metrics?.geminiConfigured && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 text-sm font-medium">
            ⚠️ GEMINI_API_KEY non configurée — l'upload et le chat resteront inactifs tant que la clé n'est pas renseignée dans le .env du backend.
          </p>
        </div>
      )}

      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card icon={FileText} label="Documents" value={metrics.documents} />
          <Card icon={CheckCircle2} label="Documents indexés" value={metrics.documentsIndexed} />
          <Card icon={MessageSquare} label="Conversations" value={metrics.conversations} />
          <Card icon={Activity} label="Requêtes IA" value={metrics.queries} />
          <Card icon={Users} label="Utilisateurs" value={metrics.users} />
          <Card
            icon={DollarSign}
            label="Coût indexation estimé"
            value={`$${metrics.estimatedIndexCostUsd}`}
            hint="Gemini File Search — storage & requêtes gratuits"
          />
        </div>
      )}
    </div>
  );
}
